import { InputHTMLAttributes, forwardRef } from "react";
import { cn } from "@drpcrd/utils/shared";
import { Typography } from "./typography";
import { Button } from "./buttons/button";
import { Badge } from "./badge";
import { AlertIcon, CheckIcon } from "./index";
import { Trash2 } from "lucide-react";

type Props = {
  disabled?: boolean;
  label?: string;
  tapLabel?: string;
  uploading: boolean;
  uploadProgress?: number;
  uploadingText?: string;
  completed?: boolean;
  fileName?: string | null;
  error?: { name: string; description: string } | null;
  onClear: () => void;
} & InputHTMLAttributes<HTMLInputElement>;

/**
 * LargeFileUploadField component for uploading and displaying file upload progress, success, and errors.
 */
export const LargeFileUploadField = forwardRef<HTMLInputElement, Props>(
  (
    {
      label = "Add your content",
      tapLabel = "Tap to choose content",
      uploading,
      uploadProgress,
      uploadingText = "Wait for your video to upload",
      completed = false,
      fileName = null,
      onClear,
      error = null,
      disabled = false,
      onChange,
      ...inputProps
    },
    ref,
  ) => (
    <div className="space-y-1.5">
      {/* Label */}
      <label htmlFor={inputProps.id} className="block flex-col">
        <Typography weight="semibold" size="sm">
          {label}
        </Typography>
      </label>

      {/* Upload area */}
      <label
        htmlFor={inputProps.id}
        className={cn(
          "border-border-primary relative block w-full overflow-hidden rounded-lg border bg-white p-3",
          !disabled && "cursor-pointer",
          uploading && "border-neutral-900",
          completed && "border-neutral-900",
        )}
      >
        {error?.name ? (
          <>
            <Typography weight="semibold" truncate>
              {error.name}
            </Typography>
            <Typography color="neutral" truncate>
              {error.description}
            </Typography>
            <Badge
              variant="error"
              label="Tap to try again"
              icon={AlertIcon}
              iconClassName="text-red-500"
              className="mt-2"
            />
          </>
        ) : completed ? (
          <>
            <Typography weight="semibold">Ready to submit!</Typography>
            <Typography color="neutral" truncate>
              {fileName}
            </Typography>
            <Badge
              variant="lightGreen"
              label="Video uploaded"
              icon={CheckIcon}
              iconClassName="text-green-500"
              className="mt-2"
            />

            {/* Remove file button */}
            <div className="absolute right-3 bottom-3 flex justify-end">
              <Button
                variant="text"
                onClick={onClear}
                icon={Trash2}
                className="p-0"
              />
            </div>
          </>
        ) : uploading ? (
          <>
            <Typography weight="semibold" truncate>
              {uploadingText}
            </Typography>
            <Typography color="neutral" truncate>
              {fileName}
            </Typography>
            <div className="relative mt-2 h-3 w-full overflow-hidden rounded-full bg-neutral-500">
              <div
                className="bg-primary absolute top-0 left-0 h-full rounded-full"
                style={{ width: `${uploadProgress ?? 0}%` }}
              />
            </div>
          </>
        ) : (
          <Typography
            weight="semibold"
            color="action"
            className="mb-6"
            truncate
          >
            {tapLabel}
          </Typography>
        )}

        {/* Hidden file input */}
        <input
          type="file"
          className="hidden"
          disabled={disabled}
          onChange={onChange}
          {...inputProps}
          ref={ref}
        />
      </label>
    </div>
  ),
);

LargeFileUploadField.displayName = "LargeFileUploadField";
